import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { addClientToBackend, updateClientToBackend } from "app/services/client.service";
import { loadUsersByRoleFromBackend } from "app/services/user.service";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import * as AppConstants from "AppConstants";
import { validateClientObject } from "utils/clientValidationUtil";
import { emailFieldProps, normalizeEmail } from "utils/emailValidationUtil";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  id: null,
  name: "",
  code: "",
  contactPersonEmail: "",
  // "" rather than null so the MUI select stays controlled and shows its placeholder.
  // @since NEXT_VERSION
  inoptraCrmUserId: "",
};

/**
 * Whether the client's saved relationship manager is absent from the dropdown's options.
 *
 * The dropdown lists only ACTIVE managers holding the MANAGER role, but the reports resolve the
 * stored id whatever that user's role and status have become. Someone who has since left the
 * manager pool therefore rendered as an EMPTY select while their id sat in the payload -- so saving
 * the client silently kept them, and the screen disagreed with the exported report. Mirrors
 * `isClientMissing`, which handles the same shape for a manager's deleted client.
 *
 * An empty list means the fetch has not resolved yet (or failed), which is not evidence that the
 * BDM is gone -- judging it then would wipe a perfectly good assignment.
 *
 * @since NEXT_VERSION
 */
const isCrmUserMissing = (bdmUsers, crmUserId) => {
  if (crmUserId === "" || crmUserId === null || crmUserId === undefined) return false;
  if (!Array.isArray(bdmUsers) || bdmUsers.length === 0) return false;
  return !bdmUsers.some((user) => parseInt(user.id, 10) === parseInt(crmUserId, 10));
};

const AddClientDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const [bdmUsers, setBdmUsers] = useState([]);

  // Every BDM, not only those already holding an account: the relationship manager is chosen from
  // the whole pool, and a BDM can own an account before anyone is placed on it.
  //
  // BDMs rather than managers since HRMS-116's rewrite -- "BDM = Client Relationship Manager - As
  // of now" -- which also makes this field the BDM-to-client mapping itself. It is one-to-one and
  // lives here, so the separate many-to-many mapping dialog it replaced is gone.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!open) return;
    loadUsersByRoleFromBackend(AppConstants.ROLE_BDM)
      .then((res) => setBdmUsers(Array.isArray(res.data) ? res.data : []))
      .catch((error) => {
        setBdmUsers([]);
        handleAxiosError(error);
      });
  }, [open]);

  useEffect(() => {
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
    if (!open) return;
    if (editObject) {
      setData({
        id: parseInt(editObject.id),
        name: editObject.name,
        code: editObject.code,
        contactPersonEmail: editObject.contactPersonEmail,
        inoptraCrmUserId: editObject.inoptraCrmUserId ?? "",
      });
    } else {
      setData(obj);
    }
  }, [open, editObject]);

  // A guard rather than part of the seeding effect above: it touches only the CRM field, so a slow
  // manager-list fetch cannot discard what has been typed into the other fields meanwhile.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!open || !editObject) return;
    if (!isCrmUserMissing(bdmUsers, editObject.inoptraCrmUserId)) return;
    setData((previous) => ({ ...previous, inoptraCrmUserId: "" }));
    setErrorAction(
      "The InOpTra manager saved as this client's relationship manager is no longer available. Please select one."
    );
  }, [open, editObject, bdmUsers]);

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const saveClientInBackend = async () => {
    // Normalize email once at submit time so the stored value is consistent
    // regardless of how the user typed it (extra spaces, capitalization).
    const payload = trimTextFields({
      ...data,
      contactPersonEmail: normalizeEmail(data.contactPersonEmail),
      // The placeholder option carries "", which is not an id. Sent as null so the backend stores
      // NULL for "no relationship manager" rather than relying on Jackson to coerce the empty
      // string. @since NEXT_VERSION
      inoptraCrmUserId: data.inoptraCrmUserId === "" || data.inoptraCrmUserId == null
        ? null
        : parseInt(data.inoptraCrmUserId, 10),
    });
    if (validateClientObject(payload) && !editObject) await addClientInvoker(payload);
    else if (validateClientObject(payload) && editObject) await updateClientInvoker(payload);
    else {
      setErrorAction("One or more required fields for a Client are missing or having invalid value");
    }
  };

  const addClientInvoker = async (clientObject) => {
    await addClientToBackend(clientObject)
      .then((data) => {
        setSuccessAction("Client added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to add client. Please try again.");
      });
  };

  const updateClientInvoker = async (data) => {
    const editObjectId = data.id;
    await updateClientToBackend(editObjectId, data)
      .then((data) => {
        setSuccessAction("Client Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to update client. Please try again.");
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Client / Account` : `Add New Client / Account`}</DialogTitle>
      <DialogContent>
        {/* <DialogContentText></DialogContentText> */}
        <MDBox my={2}>
          <TextField
            id="name"
            label="Client / Account Name"
            fullWidth
            variant="outlined"
            value={data?.name || ""}
            onBlur={trimOnBlur(setData, "name")}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="code"
            label="Code"
            fullWidth
            variant="outlined"
            value={data?.code || ""}
            onBlur={trimOnBlur(setData, "code")}
            onChange={(e) => setData({ ...data, code: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="email"
            label="Client's Contact Email"
            fullWidth
            variant="outlined"
            value={data?.contactPersonEmail || ""}
            onBlur={trimOnBlur(setData, "contactPersonEmail")}
            onChange={(e) => setData({ ...data, contactPersonEmail: e.target.value })}
            {...emailFieldProps(data?.contactPersonEmail, { required: false })}
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="inoptraCrmUserId"
            label="InOpTra's BDM / Client Relationship Manager"
            options={bdmUsers}
            getOptionValue={(user) => user.id}
            getOptionLabel={(user) => user.fullName}
            placeholder="Select an InOpTra BDM"
            value={data?.inoptraCrmUserId ?? ""}
            onChange={(e) => setData({ ...data, inoptraCrmUserId: e.target.value })}
            helperText="Optional. Shown as the InOpTra - Client Relationship Manager on exported reports, and scopes that BDM to this account."
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveClientInBackend} variant="contained">{editObject ? `Update` : `Add`}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddClientDialog);
